import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, Users, CreditCard, Ticket, FileText, Menu, LogOut, X, FolderKanban, Receipt, UserCog, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

function AdminLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    try {
      const backendUrl = getBackendUrl();
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const navigation = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Customers', icon: Users, path: '/admin/customers' },
    { name: 'Users', icon: UserCog, path: '/admin/users' },
    { name: 'Account Managers', icon: UserCircle, path: '/admin/account-managers' },
    { name: 'Subscriptions', icon: CreditCard, path: '/admin/subscriptions' },
    { name: 'Security Status', icon: Shield, path: '/admin/security-status' },
    { name: 'Tickets', icon: Ticket, path: '/admin/tickets' },
    { name: 'Support (Zoho)', icon: Ticket, path: '/admin/support' },
    { name: 'Projects', icon: FolderKanban, path: '/admin/projects' },
    { name: 'Billing (Xero)', icon: Receipt, path: '/admin/billing' },
    { name: 'Resources', icon: FileText, path: '/admin/resources' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card/50 backdrop-blur-xl">
        <div className="flex h-16 items-center gap-2 border-b border-border px-6">
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
              <span className="text-2xl font-bold text-white">entra</span>
            </div>
            <span className="text-sm font-bold text-secondary uppercase">Admin</span>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                  isActive
                    ? 'bg-secondary/10 text-secondary border border-secondary/20'
                    : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.name}
              </button>
            );
          })}
        </nav>
        
        {user && (
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-secondary/20 flex items-center justify-center">
                <span className="text-secondary font-mono font-bold">{user.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">Admin</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        )}
      </aside>

      {/* Sidebar - Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
          <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border">
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
                  <span className="text-2xl font-bold text-white">entra</span>
                </div>
                <span className="text-sm font-bold text-secondary uppercase">Admin</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="h-6 w-6 text-muted-foreground" />
              </button>
            </div>
            
            <nav className="flex-1 space-y-1 px-3 py-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                      isActive
                        ? 'bg-secondary/10 text-secondary border border-secondary/20'
                        : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-border bg-card/50 backdrop-blur-xl px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-mono font-bold">{title}</h1>
        </header>
        
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;